
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { motion } from 'framer-motion';

interface PieChartProps {
    data: { name: string; value: number; color: string }[];
    className?: string;
}

export const PieChart: React.FC<PieChartProps> = ({ data, className }) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    let cumulative = 0;
    
    // SVG Configuration
    const size = 100;
    const center = size / 2;
    const radius = 40; 
    const strokeWidth = 12; 
    const circumference = 2 * Math.PI * radius;

    return (
        <div className={`w-full h-full flex items-center justify-center relative ${className}`}>
            <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full transform -rotate-90">
                {/* Background Ring */}
                <circle 
                    cx={center} 
                    cy={center} 
                    r={radius} 
                    fill="transparent" 
                    stroke="currentColor" 
                    strokeWidth={strokeWidth} 
                    className="text-stone-100 dark:text-white/5" 
                />
                
                {data.map((item, index) => {
                    const percentage = (item.value / total) * 100;
                    if (percentage === 0) return null;
                    
                    const strokeDasharray = `${(percentage / 100) * circumference} ${circumference}`;
                    const strokeDashoffset = -((cumulative / 100) * circumference);
                    
                    cumulative += percentage;

                    return (
                        <motion.circle
                            key={item.name}
                            cx={center}
                            cy={center}
                            r={radius}
                            fill="transparent"
                            stroke={item.color}
                            strokeWidth={strokeWidth}
                            strokeDasharray={strokeDasharray}
                            strokeDashoffset={strokeDashoffset}
                            strokeLinecap="round"
                            initial={{ strokeDasharray: `0 ${circumference}` }}
                            animate={{ strokeDasharray }}
                            transition={{ duration: 1.2, delay: index * 0.1, ease: "circOut" }}
                        />
                    );
                })}
            </svg>
            
            {/* Center Content - Total Value or Label */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-[10px] text-stone-400 font-bold uppercase tracking-wider">Total</span>
            </div>
        </div>
    );
};
